import React from 'react';

import orderBy from 'lodash.orderby';
import type { FieldValues } from 'react-hook-form';

import type { QuickAddSectionProps, QuickItemRenderer } from '@/_containers';
import { AsyncQuickAddSection } from '@/_containers';
import type {
  InventoryQueryLinkerSchema,
  InventoryQuerySchema,
} from '@/_pages/settings/inventory-kits/shared/schemas/inventory';

import { Item } from './item';

import { useGetInventoriesMutation } from '../../apis';

type MultiInventoriesSectionProps<TFormValues extends FieldValues = FieldValues> = Pick<
  QuickAddSectionProps<InventoryQuerySchema, TFormValues>,
  'isEditable' | 'control' | 'fieldName' | 'title' | 'sectionTitle' | 'icon' | 'rightSlot' | 'filterId'
> & {
  items: InventoryQuerySchema[];
  showHeader?: boolean;
  sortable?: boolean; // Sortable by drag n drop
};

export function MultiInventoriesSection<TFormValues extends FieldValues = FieldValues>({
  title = 'Sub Items',
  sectionTitle = 'Sub Items',
  items = [],
  sortable = false,
  filterId,
  ...rest
}: MultiInventoriesSectionProps<TFormValues>) {
  const renderItem: QuickItemRenderer<InventoryQuerySchema> = React.useCallback(
    (item, { isEditable, removeItem }) => (
      <Item key={item.id} inventory={item} isEditing={isEditable} sortable={sortable} onDelete={removeItem} />
    ),
    [sortable]
  );

  const itemToString = React.useCallback(
    (item: InventoryQuerySchema | null) => (item?.primary_sku ? `${item.primary_sku} — ` : '') + (item?.name ?? ''),
    []
  );

  const sortValues = React.useCallback(
    // TO SEE THE DIFFERENCE, CHANGE 'name' TO 'itemToString'
    (values: InventoryQuerySchema[]) => orderBy(values, itemToString),
    [itemToString]
  );

  const keyExtractor = React.useCallback((item: InventoryQuerySchema) => item.id.toString(), []);

  const filterOptions = React.useCallback(
    (item: InventoryQueryLinkerSchema) =>
      (filterId ? item.id === filterId : false) || items.some((i) => i.id === item.id),
    [items, filterId]
  );

  const sortedItems = React.useMemo(() => (sortable ? items : sortValues(items)), [items, sortValues, sortable]);

  return (
    <AsyncQuickAddSection
      {...rest}
      sectionTitle={sectionTitle}
      title={title}
      emptyDataMessage="No Sub Items"
      items={sortedItems}
      keyExtractor={keyExtractor}
      itemToString={itemToString}
      renderItem={renderItem}
      sortValues={sortable ? undefined : sortValues}
      filterOptions={filterOptions}
      filterId={filterId}
      sortable={sortable}
      useApi={useGetInventoriesMutation}
      apiParams={{ 'filter[parents]': 'false' }}
      paginatedItems={undefined}
    />
  );
}
